import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { ProductCard } from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bell, Store, Loader2, PauseCircle, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Following() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    api.get("/feed/following").then((r) => setData(r.data)).catch(() => setData({ stores: [], products: [] }));
  }, [user, navigate]);

  const stores = data?.stores || [];
  const products = data?.products || [];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-1">
          <span className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><Sparkles className="w-5 h-5" /></span>
          <div>
            <h1 className="font-display font-bold text-2xl sm:text-3xl">New from stores you follow</h1>
            <p className="text-sm text-muted-foreground">The latest arrivals from your favourite Xurray sellers.</p>
          </div>
        </div>

        {data === null ? (
          <div className="py-24 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-muted-foreground" /></div>
        ) : stores.length === 0 ? (
          <div className="py-20 text-center" data-testid="following-empty">
            <Bell className="w-12 h-12 mx-auto mb-4 text-muted-foreground/40" />
            <h2 className="font-semibold text-lg mb-1">You're not following any stores yet</h2>
            <p className="text-sm text-muted-foreground mb-5">Tap <strong>Follow</strong> on any product page to see that store's newest products here.</p>
            <Button asChild className="rounded-full" data-testid="following-browse-btn"><Link to="/products">Browse products</Link></Button>
          </div>
        ) : (
          <>
            {/* Followed stores chips */}
            <div className="flex flex-wrap gap-2 mt-6 mb-8" data-testid="followed-stores">
              {stores.map((s) => (
                <span key={s.seller_id} className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm">
                  <Store className="w-3.5 h-3.5 text-primary" />{s.business_name}
                  {s.paused && <Badge variant="outline" className="ml-1 gap-1 text-[10px] text-amber-700 border-amber-300"><PauseCircle className="w-3 h-3" />paused</Badge>}
                </span>
              ))}
            </div>

            {products.length === 0 ? (
              <div className="py-16 text-center text-muted-foreground" data-testid="following-no-products">
                <p>No new products from your followed stores right now — check back soon!</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="following-products">
                {products.map((p) => <ProductCard key={p.product_id} product={p} />)}
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
